import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);

const PurchaseReport = () => {
  const { showToast } = useToast();
  const [purchaseData, setPurchaseData] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); 

  const [permissions, setPermissions] = useState({
    excel: false,
    pdf: false,
  });

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
    try {
      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);
      if (getPermissions.permissions) {
        parsedPermissions = getPermissions.permissions;
      } else {
        parsedPermissions = getPermissions;
      }

      const inventoryPermissions = parsedPermissions.find(
        (item) => item.title === "Reports"
      );

      const productPermissions = inventoryPermissions?.childPage?.find(
        (child) => child.title === "Purchase Report"
      );
      // console.log("search product", productPermissions)

      if (productPermissions) {
        setPermissions({
          excel: productPermissions.excel || false,
          pdf: productPermissions.pdf || false,
        });
      } else {
        console.error("Product permissions not found in localStorage");
        showToast(
          "Product permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Fetch purchase report
  const fetchPurchaseReport = async () => {
    try {
      const response = await apiClient.post(`/generatePurchaseReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setPurchaseData(response.data);
      } else {
        setPurchaseData([]);
      }
    } catch (error) {
      console.error("Failed to fetch purchase report:", error);
      showToast("Failed to fetch purchase report", "error");
    }
  };

  useEffect(() => {
    fetchPurchaseReport();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter purchase data based on search query
  const filteredPurchaseData = purchaseData.filter((purchase) => {
    const query = searchQuery.toLowerCase();
    return (
      (purchase.product_name?.toLowerCase() || "").includes(query) ||
      (purchase.purchase_reference?.toLowerCase() || "").includes(query) ||
      (purchase.subtotal?.toString() || "").includes(query) ||
      (formatDate(purchase.purchase_date)?.toLowerCase() || "").includes(query)
    );
  });

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generatePurchaseReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "purchase-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Purchase report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      console.error("Failed to export purchase report to Excel:", error);
      showToast("Failed to export purchase report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Purchase Report", 14, 20);
      doc.setFontSize(12);
      doc.text("Generated on: July 30, 2025, 12:01 PM PKT", 14, 30);

      // Table
      doc.autoTable({
        startY: 40,
        head: [
          [
            "Product Name",
            "Purchase Amount",
            "Purchase Qty",
            "Purchase Date",
            "Purchase Reference",
          ],
        ],
        body: filteredPurchaseData.map((purchase) => [
          purchase.product_name || "N/A",
          purchase.subtotal
            ? `PKR ${parseFloat(purchase.subtotal).toFixed(2)}`
            : "N/A",
          purchase.quantity || "0",
          formatDate(purchase.purchase_date),
          purchase.purchase_reference || "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 40 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("purchase-report.pdf");
      showToast("Purchase report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export purchase report to PDF", "error");
    }
  };

  // Render purchase rows
  const renderPurchaseRows = () => {
    return filteredPurchaseData.map((purchase) => (
      <tr key={purchase.purchase_reference || Math.random()}>
        <td className="productimgname" style={{ whiteSpace: "nowrap" }}>
          <div className="view-product me-2">
            <Link to="#">
              {purchase.product_image && purchase.product_image.length > 0 ? (
                <ImageWithBasePath
                  src={purchase.product_image[0]}
                  alt="product"
                />
              ) : (
                <ImageWithBasePath
                  src="https://odabill.com/assets/img/product/noimage.png"
                  alt="product"
                />
              )}
            </Link>
          </div>
          <Link to="#">
            <span className="wrap-text">{purchase.product_name || "N/A"}</span>
          </Link>
        </td>
        <td>
          <span className="wrap-text">
            {purchase.subtotal
              ? `PKR ${parseFloat(purchase.subtotal).toFixed(2)}`
              : "N/A"}
          </span>
        </td>
        <td>
          <span className="wrap-text">{purchase.quantity || "0"}</span>
        </td>
        <td>
          <span className="wrap-text">
            {formatDate(purchase.purchase_date)}
          </span>
        </td>
        <td>
          <span className="wrap-text">
            {purchase.purchase_reference || "N/A"}
          </span>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Purchase Report"
          subtitle="Manage Your Purchase Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
          permissions={permissions}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by product name and purchase reference"
                    className="form-control form-control-sm formsearch"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Product Name</th>
                    <th style={{ width: "15%" }}>Purchase Amount</th>
                    <th style={{ width: "10%" }}>Purchase Qty</th>
                    <th style={{ width: "15%" }}>Purchase Date</th>
                    <th style={{ width: "20%" }}>Purchase Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchaseData.length > 0 ? (
                    renderPurchaseRows()
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReport;
